import { DocumentSection, EntityDocument } from '@api/api/document-entity';
import { Drawer } from '@design/components/drawer/Drawer';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { DocumentViewer } from '@terminal/views/alfred/document-search/document-viewer/DocumentViewer';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Citations } from '../components/citations/Citations';
import styles from './CitationsDrawer.module.scss';

export interface SelectedCitation {
  id: string;
  document: EntityDocument;
}

export interface CitationsDrawerProps {
  citations: DocumentSection[];
  isOpen: boolean;
  onClose: () => void;
  selectedCitation?: SelectedCitation;
  onSelectCitation: (citation: SelectedCitation) => void;
}

export const CitationsDrawer = ({
  citations,
  isOpen,
  onClose,
  selectedCitation,
  onSelectCitation,
}: CitationsDrawerProps) => {
  const { t } = useTranslation(['widget']);
  const citationsRefs = useRef<(HTMLDivElement | null)[]>([]);

  const onClickCitation = (citationId: string, document: EntityDocument) => {
    onSelectCitation({ id: citationId, document });
  };

  const selectedIndex = citations.findIndex(
    citation => citation.id === selectedCitation?.id
  );
  const activeCitationIdx = selectedIndex === -1 ? undefined : selectedIndex;
  const selectedDoc = selectedCitation?.document;
  const page =
    activeCitationIdx !== undefined
      ? citations[activeCitationIdx]?.tags?.page
      : null;
  const activeCitation =
    activeCitationIdx !== undefined ? citations[activeCitationIdx] : null;
  const highlightTexts =
    !selectedDoc || selectedDoc.kind === 'presentation'
      ? []
      : [
          {
            text: activeCitation
              ? activeCitation.content
                  .trim()
                  .replace(/(\*\*)(.*?)\1/g, '$2')
                  .replace(/\s+/g, ' ')
              : '',
            pages: page ? [parseInt(page)] : undefined,
            isPrimary: true,
          },
        ];

  return (
    <Drawer
      className={styles.drawer}
      isOpen={isOpen}
      handleCloseClick={onClose}
    >
      <ErrorBoundaryWrapper>
        <div className={styles.drawerSectionWrapper}>
          <div className={styles.drawerSection}>
            <h2 className={styles.citationsHeaderTitle}>
              {t(
                'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.citations'
              )}
            </h2>
            <Citations
              citations={citations}
              onClick={onClickCitation}
              activeIdx={activeCitationIdx}
              ref={citationsRefs}
            />
          </div>
          <div className={styles.drawerSection}>
            {selectedDoc && (
              <DocumentViewer
                highlightTexts={highlightTexts}
                selectedCitation={{
                  document_id: selectedDoc.id,
                  document_name: selectedDoc.name,
                  document_type: selectedDoc.type,
                  pages: page ? [parseInt(page)] : [],
                }}
              />
            )}
          </div>
        </div>
      </ErrorBoundaryWrapper>
    </Drawer>
  );
};
